import React, { useState, useEffect } from 'react';
import { Clock, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '../hooks/useAuth';

interface SessionTimeoutModalProps {
  idleTimeSeconds?: number;
}

export const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({ idleTimeSeconds = 900 }) => {
  const { isAuthenticated, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    if (!isAuthenticated) return;

    let warningTimer: NodeJS.Timeout;
    let countdownInterval: NodeJS.Timeout;

    const resetIdleTimer = () => {
      setShowWarning(false);
      setCountdown(60);
      clearTimeout(warningTimer);
      clearInterval(countdownInterval);

      warningTimer = setTimeout(() => {
        setShowWarning(true);
        countdownInterval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              logout();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, (idleTimeSeconds - 60) * 1000);
    };

    const events = ['mousemove', 'keydown', 'scroll', 'click'];
    events.forEach((ev) => window.addEventListener(ev, resetIdleTimer));

    resetIdleTimer();

    return () => {
      clearTimeout(warningTimer);
      clearInterval(countdownInterval);
      events.forEach((ev) => window.removeEventListener(ev, resetIdleTimer));
    };
  }, [isAuthenticated, idleTimeSeconds, logout]);

  if (!showWarning) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 animate-in fade-in">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface dark:bg-[#0e101f] text-foreground p-6 shadow-2xl space-y-4 text-center relative z-50">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 mx-auto">
          <Clock className="h-6 w-6" />
        </div>

        <h3 className="text-lg font-bold text-foreground">Session Idle Timeout Warning</h3>
        <p className="text-xs text-foreground-secondary leading-relaxed">
          Due to inactivity, your enterprise session will automatically expire in{' '}
          <span className="font-mono font-bold text-amber-600 text-sm">{countdown}</span> seconds.
        </p>

        <div className="flex justify-center gap-3 pt-2">
          <Button variant="outline" size="sm" onClick={logout}>
            <LogOut className="h-4 w-4 mr-1" /> Sign Out
          </Button>
          <Button size="sm" onClick={() => setShowWarning(false)}>
            Extend Session
          </Button>
        </div>
      </div>
    </div>
  );
};
